import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import useVoyagerStore from '../../stores/useVoyagerStore';
import type { LandingAgentTurn, PriorWorkMatch } from '../../stores/slices/landingAgentSlice';
import type { LadderAnswer, PriorWorkListBlock } from '../Ladder/claims';
import TraceLink from '../agent-common/TraceLink';
import { stromaAnswerCard, stromaSectionLabel } from './askPresentation';
import { Match, refreshLabel } from './PriorWorkCard';
import { KnowledgeSections } from './SpecialistAnswerCard';

/** Earlier analyses asked for explicitly (#911): the Prior work agent's list as the server read it for this reader —
 *  who, when, the window, what each found, Open the original and Refresh — and, when the question asked what is
 *  known, what people said beside it. Nothing found says so and offers to start one. */
export function PriorWorkList({ ladder, turn, sessionId, onAsk, onRefresh }: {
  ladder: LadderAnswer; turn?: LandingAgentTurn; sessionId?: string | null;
  onAsk?: (question: string) => void; onRefresh?: (match: PriorWorkMatch) => void;
}) {
  const perspectives = useVoyagerStore((s) => s.perspectivesList);
  const [done, setDone] = React.useState(false);
  const block = ladder.prior_work as PriorWorkListBlock;
  const names = Object.fromEntries((perspectives || []).map((p) => [p.id, p.name || p.id]));
  const reask = turn?.askedRequest;
  const items = (block.analyses || []).filter((a) => a.href) as PriorWorkMatch[];
  const label = (text: string) => (
    <Typography sx={stromaSectionLabel}>{text}</Typography>
  );
  const analysed = (
    <Box data-testid="prior-work-list" sx={{ display: 'grid', gap: 1, alignContent: 'start', minWidth: 0 }}>
      {label("What's been analysed")}
      {/* every sentence the asker must see is the block's own (the server writes the answer's words from it) */}
      {block.empty && <Typography variant="body2" data-testid="prior-work-none">{block.empty}</Typography>}
      {items.map((m) => (
        <Match key={`${m.ref.source}:${m.ref.id}:${m.ref.turn_id}`} match={m} done={done || !onRefresh}
               refresh={onRefresh && reask ? refreshLabel(m, reask.perspective_id, names) : ''}
               onRefresh={() => { setDone(true); onRefresh?.(m); }} />
      ))}
      {block.start && (
        <Box data-testid="prior-work-start-offer" sx={{ display: 'grid', gap: 0.75, justifyItems: 'start' }}>
          <Typography variant="body2">{block.start.text}</Typography>
          {block.start.question && onAsk && (
            <Button size="small" variant="outlined" onClick={() => onAsk(block.start!.question!)} sx={{ textTransform: 'none' }}
                    data-testid="prior-work-start">
              {`Start one: ${block.start.question}`}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
  return (
    <Paper variant="outlined" data-testid="prior-work-answer" sx={{ p: 2, display: 'grid', gap: 1.5, ...stromaAnswerCard }}>
      {ladder.what?.headline && <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{ladder.what.headline}</Typography>}
      {block.said ? (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          <Box data-testid="prior-work-said" sx={{ display: 'grid', gap: 1, alignContent: 'start', minWidth: 0 }}>
            {label('What people said')}
            {block.said.empty
              ? <Typography variant="body2" data-testid="prior-work-said-empty">{block.said.empty}</Typography>
              : <KnowledgeSections block={block.said} />}
          </Box>
          {analysed}
        </Box>
      ) : analysed}
      {sessionId && <Box><TraceLink sessionId={sessionId} /></Box>}
    </Paper>
  );
}

export default function PriorWorkListCard({ turn }: { turn: LandingAgentTurn }) {
  const send = useVoyagerStore((s) => s.sendLandingAgentQuery);
  const ladder = turn.result?.ladder as LadderAnswer | undefined;
  if (!ladder?.prior_work) return null;
  const reask = turn.askedRequest;
  // a Refresh is #762's: the turn's own request re-sent with the earlier analysis it re-runs
  const refresh = (m: PriorWorkMatch) => {
    if (!reask || !m.refresh || m.ref.source === 'report' || !m.ref.turn_id) return;
    send(reask.query, { reask, prior: { refresh: { source: m.ref.source, id: m.ref.id, turn_id: m.ref.turn_id } } });
  };
  return <PriorWorkList ladder={ladder} turn={turn} sessionId={turn.sessionId || ladder.trace_session_id || null}
                        onAsk={(q) => send(q)} onRefresh={reask ? refresh : undefined} />;
}
